(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  const Carousel = global.NewHomeSource.Carousel;

  if (!Carousel.EventStore) {
    return;
  }

  class Arrows {
    /**
     * Initializes a new instance of the class.
     * Sets up state and event stores for carousel navigation.
     *
     * @constructor
     */
    constructor() {
      this.state = null;
      this.nextEvents = new Carousel.EventStore();
      this.previousEvents = new Carousel.EventStore();
      this.nonPassiveEvent = { passive: false };
      this.eventsToObserve = {
        onScrollChange: this.toggleArrowsVisibility.bind(this),
      };
    }

    /**
     * Initializes the carousel arrows with the provided state.
     * Sets base friction and duration from the state, and adds click event listeners.
     *
     * @param {Object} state - The state object containing configuration for friction and duration.
     * @returns {this} The instance of the class for chaining.
     */
    initialize(state) {
      this.state = state;
      this.baseFriction = this.state.get('friction');
      this.baseDuration = this.state.get('duration');
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
      this.addClickEvents();
      return this;
    }

    /**
     * Adds click event listeners to the carousel's next and previous arrow buttons.
     * Binds the appropriate scroll methods to each button's click event.
     * Event listeners are added with the specified non-passive option.
     *
     * @returns {void}
     */
    addClickEvents() {
      const nodeNode = this.state.get('rootNode');
      this.nextButton = nodeNode.querySelector('[data-component="next-arrow"]');
      this.previousButton = nodeNode.querySelector('[data-component="previous-arrow"]');

      if (this.nextButton) {
        this.nextEvents
          .add(this.nextButton, 'click', this.scrollNext.bind(this), this.nonPassiveEvent);
      }

      if (this.previousButton) {
        this.previousEvents
          .add(this.previousButton, 'click', this.scrollPrevious.bind(this), this.nonPassiveEvent);
      }
    }

    /**
     * Cleans up the carousel by removing all event listeners for next and previous arrows.
     * This method should be called when the carousel is no longer needed to prevent memory leaks.
     */
    destroy() {
      this.nextEvents.clear();
      this.previousEvents.clear();
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }

    /**
     * Scrolls the carousel to the specified index in the given direction.
     * Sets friction and duration to their base values before scrolling.
     * Notifies observers with the scroll action and data.
     *
     * @param {number} index - The index to scroll to.
     * @param {string} direction - The direction of the scroll (e.g., 'left' or 'right').
     */
    scrollTo(index, direction) {
      this.state.setFriction(this.baseFriction);
      this.state.setDuration(this.baseDuration);
      const data = {
        number: index,
        direction
      };
      this.state.notifyObservers('scrollByIndex', data);
    }

    /**
     * Scrolls the carousel to the next item.
     * Increments the current index by 1 and scrolls to the new position.
     */
    scrollNext() {
      this.state.notifyObservers('carouselNavigation', {
        originator: 'nextArrow',
      });
      const index = this.state.get('index');
      const next = index.add(1).get();
      this.scrollTo(next, -1);
    }

    /**
     * Scrolls the carousel to the previous item.
     * Retrieves the current index from the state, decrements it by one,
     * and scrolls to the resulting index.
     */
    scrollPrevious() {
      this.state.notifyObservers('carouselNavigation', {
        originator: 'previousArrow',
      });
      const index = this.state.get('index');
      const prev = index.add(-1).get();
      this.scrollTo(prev, 1);
    }

    /**
     * Updates the state by invoking the callback associated with the given topic.
     *
     * @param {Object} data - The data to be passed to the callback function.
     * @param {string} topic - The topic whose associated callback should be invoked.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }

    /**
     * Hides the "next" button in the carousel by adding the 'nhs-u-opacity-0' CSS class,
     * making it fully transparent.
     */
    hideNextButton() {
      if (!this.nextButton) {
        return;
      }

      this.nextButton.classList.add('nhs-u-opacity-0');
    }

    /**
     * Displays the "next" button in the carousel by removing the opacity class.
     * Assumes that `this.nextButton` is a DOM element and that the class 'nhs-u-opacity-0'
     * controls the button's visibility.
     */
    showNextButton() {
      if (!this.nextButton) {
        return;
      }

      this.nextButton.classList.remove('nhs-u-opacity-0');
    }

    /**
     * Hides the previous button in the carousel by adding the 'nhs-u-opacity-0' CSS class,
     * which sets its opacity to 0 and makes it invisible to the user.
     */
    hidePreviousButton() {
      if (!this.previousButton) {
        return;
      }

      this.previousButton.classList.add('nhs-u-opacity-0');
    }

    /**
     * Displays the previous button in the carousel by removing the 'nhs-u-opacity-0' class,
     * making it visible to the user.
     */
    showPreviousButton() {
      if (!this.previousButton) {
        return;
      }

      this.previousButton.classList.remove('nhs-u-opacity-0');
    }

    /**
     * Shows or hides the carousel navigation arrows based on scroll availability.
     *
     * @param {Object} data - The data object containing scroll state.
     * @param {boolean} data.canScrollPrevious - Indicates if scrolling to the previous item is possible.
     * @param {boolean} data.canScrollNext - Indicates if scrolling to the next item is possible.
     */
    toggleArrowsVisibility(data) {
      if (data.canScrollPrevious) {
        this.showPreviousButton();
      } else {
        this.hidePreviousButton();
      }

      if (data.canScrollNext) {
        this.showNextButton();
      } else {
        this.hideNextButton();
      }
    }
  }
  global.NewHomeSource.Carousel.Arrows = Arrows;
}(window));
